"use client";

import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Volume2, VolumeX, Play, Square, Moon, Sparkles, Sun, Timer, Maximize2, Minimize2, Heart, Waves, Wind } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { audioSynthesizer, type SoundType } from "@/lib/audio-synthesizer";

interface SleepToolsDialogProps {
  children?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const soundPresets: { type: SoundType; label: string; tag: string; icon: React.ReactNode }[] = [
  {
    type: 'white',
    label: 'White Noise',
    tag: 'Masks room noise',
    icon: <Wind className="h-4 w-4 text-blue-500" />,
  },
  {
    type: 'pink',
    label: 'Pink Noise',
    tag: 'Gentle rain sound',
    icon: <Waves className="h-4 w-4 text-teal-500" />,
  },
  {
    type: 'brown',
    label: 'Brown Noise',
    tag: 'Deep ocean rumble',
    icon: <Moon className="h-4 w-4 text-indigo-500" />,
  },
  {
    type: 'womb',
    label: 'Womb Rhythm',
    tag: '65 BPM heartbeat',
    icon: <Heart className="h-4 w-4 text-rose-500" />,
  },
];

const nightLightColors = [
  { name: 'Warm Amber', value: '#FF9E2C', text: '#5C3000' },
  { name: 'Candlelight', value: '#FFB347', text: '#4D2400' },
  { name: 'Gentle Peach', value: '#FFDAB9', text: '#593322' },
  { name: 'Soft Red', value: '#FF6B6B', text: '#FFFFFF' },
  { name: 'Lunar Glow', value: '#FFF8DC', text: '#4A4637' },
];

const timerOptions = [
  { label: 'Off', minutes: 0 },
  { label: '15m', minutes: 15 },
  { label: '30m', minutes: 30 },
  { label: '45m', minutes: 45 },
  { label: '60m', minutes: 60 },
];

export function SleepToolsDialog({ children, open: controlledOpen, onOpenChange: setControlledOpen }: SleepToolsDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = setControlledOpen ?? setInternalOpen;

  // Sound machine state
  const [selectedSound, setSelectedSound] = useState<SoundType>('pink');
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.6);
  const [selectedTimerMinutes, setSelectedTimerMinutes] = useState(0);
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  // Night light state
  const [selectedColor, setSelectedColor] = useState(nightLightColors[0]);
  const [brightness, setBrightness] = useState(70);
  const [isFullScreenLight, setIsFullScreenLight] = useState(false);
  const [showLightControls, setShowLightControls] = useState(true);
  const wakeLockRef = useRef<any>(null);

  // Sync state with audioSynthesizer
  useEffect(() => {
    setIsPlaying(audioSynthesizer.isPlaying());
    const current = audioSynthesizer.getCurrentSound();
    if (current) {
      setSelectedSound(current);
    }
    setRemainingSeconds(audioSynthesizer.getRemainingSeconds());

    audioSynthesizer.setOnTimeUpdate((secs) => {
      setRemainingSeconds(secs);
      if (secs === 0 && !audioSynthesizer.isPlaying()) {
        setIsPlaying(false);
      }
    });

    return () => {
      audioSynthesizer.setOnTimeUpdate(null);
    };
  }, []);

  // Screen Wake Lock API for full-screen night light
  useEffect(() => {
    if (isFullScreenLight) {
      if ('wakeLock' in navigator) {
        (navigator as any).wakeLock.request('screen').then((lock: any) => {
          wakeLockRef.current = lock;
        }).catch((err: any) => {
          console.warn('Wake Lock error:', err);
        });
      }
    } else {
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
        wakeLockRef.current = null;
      }
    }

    return () => {
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
        wakeLockRef.current = null;
      }
    };
  }, [isFullScreenLight]);

  const handleTogglePlay = () => {
    if (isPlaying) {
      audioSynthesizer.fadeAndStop(0.4);
      setIsPlaying(false);
    } else {
      audioSynthesizer.start(selectedSound, volume, selectedTimerMinutes);
      setIsPlaying(true);
    }
  };

  const handleSelectSound = (type: SoundType) => {
    setSelectedSound(type);
    if (isPlaying) {
      audioSynthesizer.start(type, volume, selectedTimerMinutes);
    }
  };

  const handleVolumeChange = (newVol: number[]) => {
    const val = newVol[0];
    setVolume(val);
    audioSynthesizer.setVolume(val);
  };

  const handleTimerSelect = (mins: number) => {
    setSelectedTimerMinutes(mins);
    if (isPlaying) {
      audioSynthesizer.start(selectedSound, volume, mins);
    }
  };

  const formatCountdown = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}m ${s.toString().padStart(2, '0')}s`;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        {children && <DialogTrigger asChild>{children}</DialogTrigger>}
        <DialogContent className="sm:max-w-[440px] max-w-[94vw] max-h-[90dvh] p-0 flex flex-col rounded-2xl overflow-hidden shadow-2xl border bg-background">
          <DialogHeader className="p-4 pb-3 border-b shrink-0 bg-card/50">
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                <Sparkles className="h-4 w-4" />
              </div>
              <div>
                <DialogTitle className="text-base font-semibold text-foreground">In-App Sleep Tools</DialogTitle>
                <p className="text-xs text-muted-foreground">Free soothing sounds & nursery night light</p>
              </div>
            </div>
          </DialogHeader>

          <Tabs defaultValue="sound" className="w-full flex-1 flex flex-col min-h-0 overflow-hidden">
            <div className="px-4 pt-3 pb-1 shrink-0">
              <TabsList className="grid grid-cols-2 w-full h-10 p-1 bg-muted/70 rounded-xl">
                <TabsTrigger
                  value="sound"
                  className="h-full rounded-lg gap-1.5 text-xs font-semibold data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-xs min-h-0 no-min-touch"
                >
                  <Volume2 className="h-3.5 w-3.5" />
                  Sound Machine
                </TabsTrigger>
                <TabsTrigger
                  value="light"
                  className="h-full rounded-lg gap-1.5 text-xs font-semibold data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-xs min-h-0 no-min-touch"
                >
                  <Sun className="h-3.5 w-3.5" />
                  Night Light
                </TabsTrigger>
              </TabsList>
            </div>

            {/* SOUND MACHINE TAB */}
            <TabsContent value="sound" className="flex-1 overflow-y-auto px-4 pt-2 pb-5 space-y-3.5 min-h-0 focus-visible:outline-none">
              {/* Presets Grid */}
              <div className="grid grid-cols-2 gap-2">
                {soundPresets.map((preset) => {
                  const isSelected = selectedSound === preset.type;
                  return (
                    <button
                      key={preset.type}
                      type="button"
                      aria-label={preset.label}
                      onClick={() => handleSelectSound(preset.type)}
                      className={`flex flex-col justify-between p-2.5 rounded-xl border text-left transition-all ${
                        isSelected
                          ? 'border-primary bg-primary/10 shadow-xs ring-1 ring-primary/40'
                          : 'border-border/70 bg-card hover:bg-muted/50 hover:border-border'
                      }`}
                    >
                      <div className="flex items-center justify-between w-full mb-1">
                        <span className="font-semibold text-xs text-foreground">{preset.label}</span>
                        {preset.icon}
                      </div>
                      <span className="text-[11px] text-muted-foreground leading-none">{preset.tag}</span>
                    </button>
                  );
                })}
              </div>

              {/* Volume Slider */}
              <div className="space-y-1.5 bg-muted/30 p-2.5 rounded-xl border border-border/60">
                <div className="flex items-center justify-between text-xs">
                  <Label className="flex items-center gap-1.5 text-xs font-medium text-foreground">
                    {volume === 0 ? <VolumeX className="h-3.5 w-3.5 text-muted-foreground" /> : <Volume2 className="h-3.5 w-3.5 text-primary" />}
                    Sound Volume
                  </Label>
                  <span className="text-muted-foreground font-mono text-[11px]">{Math.round(volume * 100)}%</span>
                </div>
                <Slider
                  value={[volume]}
                  onValueChange={handleVolumeChange}
                  min={0.05}
                  max={1.0}
                  step={0.05}
                  className="w-full py-1"
                />
              </div>

              {/* Timer Buttons */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <Label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                    <Timer className="h-3.5 w-3.5 text-muted-foreground" />
                    Auto-Off Timer
                  </Label>
                  {remainingSeconds > 0 && isPlaying && (
                    <span className="text-xs font-semibold text-primary font-mono">
                      {formatCountdown(remainingSeconds)}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-5 gap-1.5">
                  {timerOptions.map((opt) => (
                    <Button
                      key={opt.minutes}
                      type="button"
                      size="sm"
                      variant={selectedTimerMinutes === opt.minutes ? "default" : "outline"}
                      className={`h-8 text-xs px-1 rounded-lg no-min-touch ${
                        selectedTimerMinutes === opt.minutes
                          ? "bg-primary text-primary-foreground font-medium shadow-xs"
                          : "border-border/70 hover:bg-muted/50 text-foreground"
                      }`}
                      onClick={() => handleTimerSelect(opt.minutes)}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Main Play / Stop Button */}
              <Button
                type="button"
                size="lg"
                onClick={handleTogglePlay}
                className={`w-full h-11 text-sm font-semibold rounded-xl shadow-md transition-all gap-2 ${
                  isPlaying
                    ? 'bg-destructive hover:bg-destructive/90 text-destructive-foreground shadow-destructive/20'
                    : 'bg-primary hover:bg-primary/90 text-primary-foreground shadow-primary/20'
                }`}
              >
                {isPlaying ? (
                  <>
                    <Square className="h-4 w-4 fill-current" />
                    Stop Sound
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 fill-current" />
                    Play {soundPresets.find(p => p.type === selectedSound)?.label}
                  </>
                )}
              </Button>
            </TabsContent>

            {/* NIGHT LIGHT TAB */}
            <TabsContent value="light" className="flex-1 overflow-y-auto px-4 pt-2 pb-5 space-y-3.5 min-h-0 focus-visible:outline-none">
              {/* Color Preview & Presets */}
              <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground">Ambient Color Palette</Label>
                <div className="grid grid-cols-5 gap-2">
                  {nightLightColors.map((color) => {
                    const isSelected = selectedColor.name === color.name;
                    return (
                      <button
                        key={color.name}
                        type="button"
                        onClick={() => setSelectedColor(color)}
                        className={`h-10 rounded-xl border-2 transition-all flex items-center justify-center p-1 relative ${
                          isSelected
                            ? 'border-primary ring-2 ring-primary/30 scale-105 shadow-xs'
                            : 'border-border/60 hover:scale-102 opacity-90 hover:opacity-100'
                        }`}
                        style={{ backgroundColor: color.value }}
                        title={color.name}
                      >
                        {isSelected && <div className="h-2 w-2 rounded-full bg-black/60 shadow-xs" />}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-muted-foreground text-center">
                  Selected: <span className="font-semibold text-foreground">{selectedColor.name}</span> (melatonin-friendly)
                </p>
              </div>

              {/* Brightness Control */}
              <div className="space-y-1.5 bg-muted/30 p-2.5 rounded-xl border border-border/60">
                <div className="flex items-center justify-between text-xs">
                  <Label className="flex items-center gap-1.5 text-xs font-medium text-foreground">
                    <Sun className="h-3.5 w-3.5 text-amber-500" />
                    Light Brightness
                  </Label>
                  <span className="text-muted-foreground font-mono text-[11px]">{brightness}%</span>
                </div>
                <Slider
                  value={[brightness]}
                  onValueChange={(val) => setBrightness(val[0])}
                  min={10}
                  max={100}
                  step={5}
                  className="w-full py-1"
                />
              </div>

              {/* Fullscreen Night Light Button */}
              <Button
                type="button"
                size="lg"
                onClick={() => {
                  setIsFullScreenLight(true);
                  setOpen(false);
                }}
                className="w-full h-11 text-sm font-semibold rounded-xl gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-md shadow-amber-500/20"
              >
                <Maximize2 className="h-4 w-4" />
                Launch Full-Screen Night Light
              </Button>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* FULL SCREEN NIGHT LIGHT OVERLAY */}
      {isFullScreenLight && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed inset-0 z-[99999] transition-colors duration-500 flex flex-col justify-between p-6 select-none"
          style={{
            backgroundColor: selectedColor.value,
            opacity: brightness / 100,
          }}
          onClick={() => setShowLightControls(!showLightControls)}
        >
          {/* Top Info / Close */}
          <div className={`flex justify-between items-center transition-opacity duration-300 ${showLightControls ? 'opacity-90' : 'opacity-0'}`}>
            <div
              className="text-xs font-medium px-3 py-1 rounded-full backdrop-blur-md bg-black/15 shadow-sm"
              style={{ color: selectedColor.text }}
            >
              LullabAI Nursery Light • Tap screen to toggle controls
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setIsFullScreenLight(false);
              }}
              className="gap-1 text-xs shadow-md backdrop-blur-md bg-black/20 text-white hover:bg-black/30 border-none rounded-lg"
            >
              <Minimize2 className="h-3.5 w-3.5" />
              Exit
            </Button>
          </div>

          {/* Bottom Floating Control Bar */}
          <div
            className={`mx-auto max-w-sm w-full bg-black/30 backdrop-blur-md p-3.5 rounded-2xl border border-white/20 shadow-2xl space-y-3 transition-opacity duration-300 ${
              showLightControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 text-white text-xs">
              <Sun className="h-3.5 w-3.5 shrink-0 opacity-80" />
              <Slider
                value={[brightness]}
                onValueChange={(v) => setBrightness(v[0])}
                min={10}
                max={100}
                step={5}
                className="w-full"
              />
              <span className="font-mono text-[11px] w-8 text-right opacity-90">{brightness}%</span>
            </div>

            <div className="flex justify-center gap-2">
              {nightLightColors.map((color) => (
                <button
                  key={color.name}
                  onClick={() => setSelectedColor(color)}
                  className={`h-7 w-7 rounded-full border-2 transition-transform ${
                    selectedColor.name === color.name ? 'scale-110 border-white' : 'border-transparent opacity-80'
                  }`}
                  style={{ backgroundColor: color.value }}
                  title={color.name}
                />
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
